import React from 'react'

import { Source } from 'types/GlobalSource'

import Box from 'components_new/atoms/Box'
import Collapse from 'components_new/atoms/Collapse'
import Text from 'components_new/atoms/Text'

import AuthStep, { TAuthValidation } from './AuthStep'
import CustomAuthStep, { TCustomAuth } from './CustomAuthStep'
import NameStep from './NameStep'
import SelectSource from './SelectSource'

import { ESourceStep, getStepMeta, TSelectedSource } from '../lib'

import { ESourceCategory } from 'utils/sources'

export type TNewSourceValues = {
  authParamValues: Record<string, string>
  customAuth: TCustomAuth
  displayName: string
}

interface ContentProps {
  category: ESourceCategory
  currentStep?: ESourceStep
  duplicateName: boolean
  onChange: <K extends keyof TNewSourceValues>(
    field: K,
    value: TNewSourceValues[K]
  ) => void
  onSelect: (selected: TSelectedSource) => void
  onValidateAuth: () => void
  search: string
  selected: TSelectedSource | null
  sources: { [id: string]: Source }
  validation: TAuthValidation
  values: TNewSourceValues
}

const Content = (props: ContentProps) => {
  const {
    category,
    currentStep,
    duplicateName,
    onChange,
    onSelect,
    onValidateAuth,
    search,
    selected,
    sources,
    validation,
    values
  } = props

  if (!selected || !currentStep) {
    return (
      <SelectSource
        category={category}
        onSelect={onSelect}
        search={search}
        sources={sources}
      />
    )
  }

  const { description, title } = getStepMeta(currentStep)

  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Text gutterBottom={true} variant="h5">
          {title}
        </Text>
        <Text color="text.secondary" variant="body1">
          {description}
        </Text>
      </Box>

      {/*
        Every step stays mounted behind a Collapse so that going back and forth
        keeps what has already been filled in.
      */}
      <Collapse in={currentStep === ESourceStep.AUTH}>
        <AuthStep
          authParams={selected.source.included.auth_params}
          onChange={(authParamValues) =>
            onChange('authParamValues', authParamValues)
          }
          onValidate={onValidateAuth}
          validation={validation}
          values={values.authParamValues}
        />
      </Collapse>

      <Collapse in={currentStep === ESourceStep.CUSTOM_AUTH}>
        <CustomAuthStep
          onChange={(customAuth) => onChange('customAuth', customAuth)}
          values={values.customAuth}
        />
      </Collapse>

      <Collapse in={currentStep === ESourceStep.NAME}>
        <NameStep
          duplicate={duplicateName}
          onChange={(displayName) => onChange('displayName', displayName)}
          value={values.displayName}
        />
      </Collapse>
    </Box>
  )
}

export default Content
